import React, { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, History, UserCircle2 } from "lucide-react";

export default function ClientLog({ showLogs, logs = [], data = [] }) {
  const getExpertName = (expertId) => {
    if (!expertId || Number(expertId) === 0) return "--";

    return (
      data.find((i) => Number(i.expertID) === Number(expertId))?.expertName ||
      "--"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";

    const parsedDate = new Date(dateString.replace(" ", "T"));
    if (Number.isNaN(parsedDate.getTime())) return dateString;

    return parsedDate.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const timeA = new Date((a.timestamp || "").replace(" ", "T")).getTime();
      const timeB = new Date((b.timestamp || "").replace(" ", "T")).getTime();
      return timeB - timeA;
    });
  }, [logs]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[6px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 14, scale: 0.985 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex h-[82vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)]"
        >
          {/* Decorative background */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-r from-[#365561] via-[#2f4a55] to-[#0f172a]" />
            <div className="absolute -left-16 top-10 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-indigo-400/10 blur-3xl" />
          </div>

          {/* Header */}
          <div className="relative z-10 border-b border-white/10 px-7 py-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-inner shadow-white/5 backdrop-blur">
                    <History size={20} />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold tracking-tight">
                      Client History Logs
                    </h2>
                    <p className="mt-1 text-sm text-slate-300">
                      Activity timeline for this client record
                    </p>
                  </div>
                </div>

                <div className="absolute inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-slate-200 backdrop-blur">
                  {sortedLogs.length} {sortedLogs.length === 1 ? "entry" : "entries"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => showLogs(false)}
                className="rounded-xl border border-white/10 bg-white/10 p-2.5 text-slate-200 transition duration-200 hover:bg-white/20 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Table area */}
          <div className="relative z-10 flex-1 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
            <div className="h-full overflow-auto">
              <table className="min-w-full table-fixed border-separate border-spacing-0">
                <colgroup>
                  <col className="w-[240px]" />
                  <col className="w-[170px]" />
                  <col />
                  <col className="w-[250px]" />
                </colgroup>

                <thead className="sticky top-0 z-20">
                  <tr>
                    <th className="border-b border-slate-200/80 bg-white/80 px-7 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500 backdrop-blur-xl">
                      Date
                    </th>
                    <th className="border-b border-slate-200/80 bg-white/80 px-7 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500 backdrop-blur-xl">
                      Action by
                    </th>
                    <th className="border-b border-slate-200/80 bg-white/80 px-7 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500 backdrop-blur-xl">
                      Detail
                    </th>
                    <th className="border-b border-slate-200/80 bg-white/80 px-7 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500 backdrop-blur-xl">
                      Expert
                    </th>
                  </tr>
                </thead>

                {sortedLogs.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan={4} className="px-7 py-20 text-center">
                        <div className="mx-auto max-w-sm">
                          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 shadow-inner">
                            <History className="text-slate-400" size={26} />
                          </div>
                          <h3 className="mt-5 text-base font-semibold text-slate-800">
                            No history logs available
                          </h3>
                          <p className="mt-2 text-sm leading-6 text-slate-500">
                            Once updates are made to this client, the activity log
                            will appear here in descending date order.
                          </p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <motion.tbody
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: {},
                      show: {
                        transition: {
                          staggerChildren: 0.045,
                        },
                      },
                    }}
                  >
                    {sortedLogs.map((item, index) => (
                      <motion.tr
                        key={`${item.timestamp}-${index}`}
                        variants={{
                          hidden: { opacity: 0, y: 10 },
                          show: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.24, ease: "easeOut" }}
                        className="group"
                      >
                        <td className="border-b border-slate-100 px-7 py-3 align-top transition-colors duration-200 group-hover:bg-slate-50/80">
                          <div className="text-sm font-medium text-slate-800">
                            {formatDate(item.timestamp)}
                          </div>
                          {/* <div className="mt-1 text-xs text-slate-400">
                            Log entry
                          </div> */}
                        </td>

                        <td className="border-b border-slate-100 px-7 py-3 align-top transition-colors duration-200 group-hover:bg-slate-50/80">
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold tracking-wide shadow-sm ${
                              item.changedFrom && item.changedFrom !== "Client"
                                ? "border-slate-300 bg-[#E6EEF1] text-[#365561]"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {item.changedFrom || "Client"}
                          </span>
                        </td>

                        <td className="border-b border-slate-100 px-7 py-3 align-top transition-colors duration-200 group-hover:bg-slate-50/80">
                          <div className="max-w-none whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
                            {item.detail || "--"}
                          </div>
                        </td>

                        <td className="border-b border-slate-100 px-7 py-3 align-top transition-colors duration-200 group-hover:bg-slate-50/80">
                          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                            <UserCircle2 size={16} className="text-slate-400" />
                            <span className="text-sm font-medium text-slate-700">
                              {getExpertName(item.expert)}
                            </span>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </motion.tbody>
                )}
              </table>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}